import { useMemo } from "react";

import { STATEMENT_CONT_PAGE_ROWS, STATEMENT_FIRST_PAGE_ROWS } from "../constants/cardStatement.constants";
import type { CardStatementTransaction } from "../types/card-statement.types";

export interface UseStatementPaginationResult {
  pages: CardStatementTransaction[][];
  pageCount: number;
}

/**
 * Splits the movements into A4 pages. The first page carries the masthead, the parties block and the summary
 * strip, so it holds fewer rows than the continuation pages.
 *
 * The capacities are exact rather than measured because row height is pinned in `statementPrint.css`. If a
 * column is ever allowed to wrap, replace this with measured chunking (render off-screen, read offsetHeight
 * per row, pack against the measured budget) — the swap is contained to this function.
 */
export const chunkStatementTransactions = (
  transactions: CardStatementTransaction[],
): CardStatementTransaction[][] => {
  // An empty statement is still a valid document: one page with an empty-state row.
  if (transactions.length === 0) return [[]];

  const pages: CardStatementTransaction[][] = [];
  let cursor = 0;

  while (cursor < transactions.length) {
    const capacity = pages.length === 0 ? STATEMENT_FIRST_PAGE_ROWS : STATEMENT_CONT_PAGE_ROWS;
    const take = Math.min(capacity, transactions.length - cursor);
    pages.push(transactions.slice(cursor, cursor + take));
    cursor += take;
  }

  return pages;
};

export const useStatementPagination = (
  transactions: CardStatementTransaction[],
): UseStatementPaginationResult =>
  useMemo(() => {
    const pages = chunkStatementTransactions(transactions);
    return { pages, pageCount: pages.length };
  }, [transactions]);
